import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';

const PatientDetailsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patientDetails, setPatientDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [associatedCases, setAssociatedCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [casesError, setCasesError] = useState(null);

  const [associatedHealthSystems, setAssociatedHealthSystems] = useState([]);
  const [loadingHealthSystems, setLoadingHealthSystems] = useState(true);
  const [healthSystemsError, setHealthSystemsError] = useState(null);

  const [associatedZoomVisits, setAssociatedZoomVisits] = useState([]);
  const [loadingZoomVisits, setLoadingZoomVisits] = useState(true);
  const [zoomVisitsError, setZoomVisitsError] = useState(null);

  const [accessLogs, setAccessLogs] = useState([]);
  const [loadingAccessLogs, setLoadingAccessLogs] = useState(true);
  const [accessLogsError, setAccessLogsError] = useState(null);

  const [activeTab, setActiveTab] = useState('cases');


  useEffect(() => {
    const fetchPatientDetails = async () => {
      try {
        setIsLoading(true);
        // Assuming an API endpoint for fetching patient details by ID
        const response = await adminApi.getPatientDetails(patientId);
        if (response.data.success) {
          setPatientDetails(response.data.patient);
        } else {
          setError(response.data?.message || 'Failed to load patient details');
        }
      } catch (err) {
        console.error('Error fetching patient details:', err);
        setError(`Error: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (patientId) {
      fetchPatientDetails();
    }
  }, [patientId]);

  // Fetch associated cases
  useEffect(() => {
    const fetchAssociatedCases = async () => {
      try {
        setLoadingCases(true);
        // Assuming an API endpoint for fetching cases by patient ID
        const response = await adminApi.getPatientCases(patientId);
        if (response.data.success) {
          setAssociatedCases(response.data.cases);
        } else {
          setCasesError(response.data?.message || 'Failed to load associated cases');
        }
      } catch (err) {
        console.error('Error fetching associated cases:', err);
        setCasesError(`Error: ${err.message}`);
      } finally {
        setLoadingCases(false);
      }
    };

    if (patientId) {
      fetchAssociatedCases();
    }
  }, [patientId]);

  // Fetch associated health systems
  useEffect(() => {
    const fetchAssociatedHealthSystems = async () => {
      try {
        setLoadingHealthSystems(true);
        // Assuming an API endpoint for fetching health systems by patient ID
        const response = await adminApi.getPatientHealthSystems(patientId);
        if (response.data.success) {
          setAssociatedHealthSystems(response.data.health_systems);
        } else {
          setHealthSystemsError(response.data?.message || 'Failed to load associated health systems');
        }
      } catch (err) {
        console.error('Error fetching associated health systems:', err);
        setHealthSystemsError(`Error: ${err.message}`);
      } finally {
        setLoadingHealthSystems(false);
      }
    };

    if (patientId) {
      fetchAssociatedHealthSystems();
    }
  }, [patientId]);

  // Fetch associated Zoom visits
  useEffect(() => {
    const fetchAssociatedZoomVisits = async () => {
      try {
        setLoadingZoomVisits(true);
        // Assuming an API endpoint for fetching Zoom visits by patient ID
        const response = await adminApi.getPatientZoomVisits(patientId);
        if (response.data.success) {
          setAssociatedZoomVisits(response.data.zoom_visits);
        } else {
          setZoomVisitsError(response.data?.message || 'Failed to load associated Zoom visits');
        }
      } catch (err) {
        console.error('Error fetching associated Zoom visits:', err);
        setZoomVisitsError(`Error: ${err.message}`);
      } finally {
        setLoadingZoomVisits(false);
      }
    };

    if (patientId) {
      fetchAssociatedZoomVisits();
    }
  }, [patientId]);

  // Fetch access logs
  useEffect(() => {
    const fetchAccessLogs = async () => {
      try {
        setLoadingAccessLogs(true);
        // Assuming an API endpoint for fetching access logs by patient ID
        const response = await adminApi.getPatientAccessLogs(patientId);
        if (response.data.success) {
          setAccessLogs(response.data.data);
        } else {
          setAccessLogsError(response.data?.message || 'Failed to load access logs');
        }
      } catch (err) {
        console.error('Error fetching access logs:', err);
        setAccessLogsError(`Error: ${err.message}`);
      } finally {
        setLoadingAccessLogs(false);
      }
    };

    if (patientId) {
      fetchAccessLogs();
    }
  }, [patientId]);

   // Format date for display
  const formatDate = (dateString, dateOnly = false, dobFormat = false) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    
    if (dobFormat) {
      // Format as "Jun 5, 1925"
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    return dateOnly ? date.toLocaleDateString() : date.toLocaleString();
  };

  const handleBackToList = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 font-size-18">
                <button
                  className="btn btn-sm btn-light me-2"
                  onClick={handleBackToList}
                >
                  <i className="fas fa-arrow-left me-1"></i>
                </button>
                {patientDetails ? `${patientDetails.first_name} ${patientDetails.last_name} (${formatDate(patientDetails.dob, false, true)})` : 'Patient Details'}
              </h4>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="d-flex justify-content-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : patientDetails ? (
          <div className="row">
            <div className="col-xl-4">
              <div className="card overflow-hidden">
                <div className="bg-primary bg-soft">
                  <div className="row">
                    <div className="">
                      <div className="text-white p-3">
                        <p className="mb-0">MiraSol ID: {patientDetails.id}</p>
                        <p className="mb-0">Core Server ID: {patientDetails.patient_id}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="pt-4">
                        {/* Basic Information */}
                        <div className="mb-4">
                          <h5 className="font-size-14 mb-3">
                            <i className="fas fa-address-card me-2"></i>
                            Basic Information
                          </h5>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Full Name:</div>
                            <div className="col-md-8">{patientDetails.first_name} {patientDetails.middle_name ? patientDetails.middle_name + ' ' : ''}{patientDetails.last_name}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Medical Record #:</div>
                            <div className="col-md-8">{patientDetails.ehr_medical_record_number || 'N/A'}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Date of Birth:</div>
                            <div className="col-md-8">{formatDate(patientDetails.dob, false, true)}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Gender:</div>
                            <div className="col-md-8">{patientDetails.gender || 'N/A'}</div>
                          </div>
                        </div>

                        {/* Contact Information */}
                        <div className="mb-4">
                          <h5 className="font-size-14 mb-3">
                            <i className="fas fa-phone me-2"></i>
                            Contact Information
                          </h5>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Primary Phone:</div>
                            <div className="col-md-8">
                              {patientDetails.phone || 'N/A'}
                              {patientDetails.phone &&
                                <span className="ms-2 badge bg-info">
                                  SMS: {patientDetails.phone_recieve_sms ? 'Yes' : 'No'}
                                </span>
                              }
                            </div>
                          </div>
                          {patientDetails.phone_2 && (
                            <div className="row mb-2">
                              <div className="col-md-4 text-muted">Secondary Phone:</div>
                              <div className="col-md-8">
                                {patientDetails.phone_2}
                                <span className="ms-2 badge bg-info">
                                  SMS: {patientDetails.phone_2_recieve_sms ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>
                          )}
                          {patientDetails.phone_3 && (
                            <div className="row mb-2">
                              <div className="col-md-4 text-muted">Tertiary Phone:</div>
                              <div className="col-md-8">
                                {patientDetails.phone_3}
                                <span className="ms-2 badge bg-info">
                                  SMS: {patientDetails.phone_3_recieve_sms ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>
                          )}
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Primary Email:</div>
                            <div className="col-md-8">{patientDetails.email || 'N/A'}</div>
                          </div>
                          {patientDetails.email_2 && (
                            <div className="row mb-2">
                              <div className="col-md-4 text-muted">Secondary Email:</div>
                              <div className="col-md-8">{patientDetails.email_2}</div>
                            </div>
                          )}
                          {patientDetails.email_3 && (
                            <div className="row mb-2">
                              <div className="col-md-4 text-muted">Tertiary Email:</div>
                              <div className="col-md-8">{patientDetails.email_3}</div>
                            </div>
                          )}
                        </div>

                        {/* Address Information */}
                        <div className="mb-4">
                          <h5 className="font-size-14 mb-3">
                            <i className="fas fa-map-marker-alt me-2"></i>
                            Address Information
                          </h5>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Street Address:</div>
                            <div className="col-md-8">{patientDetails.street_address || 'N/A'}</div>
                          </div>
                          {patientDetails.street_address_2 && (
                            <div className="row mb-2">
                              <div className="col-md-4 text-muted">Street Address 2:</div>
                              <div className="col-md-8">{patientDetails.street_address_2}</div>
                            </div>
                          )}
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">City:</div>
                            <div className="col-md-8">{patientDetails.city || 'N/A'}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">State:</div>
                            <div className="col-md-8">{patientDetails.state || 'N/A'}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Zip Code:</div>
                            <div className="col-md-8">{patientDetails.zip_code || 'N/A'}</div>
                          </div>
                        </div>

                        {/* System Information */}
                        <div className="mb-4">
                          <h5 className="font-size-14 mb-3">
                            <i className="fas fa-info-circle me-2"></i>
                            System Information
                          </h5>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Created:</div>
                            <div className="col-md-8">{formatDate(patientDetails.created_at)}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-4 text-muted">Last Updated:</div>
                            <div className="col-md-8">{formatDate(patientDetails.updated_at)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Health System Card */}
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title mb-4">
                    <i className="fas fa-hospital me-2"></i>
                    Health System
                  </h4>
                  {loadingHealthSystems ? (
                    <div className="d-flex justify-content-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : healthSystemsError ? (
                    <div className="alert alert-danger">{healthSystemsError}</div>
                  ) : associatedHealthSystems.length === 0 ? (
                    <div className="alert alert-info">No health system associated with this patient.</div>
                  ) : (
                    <div className="health-system-card">
                      <h5 className="mb-1">{associatedHealthSystems[0].name || 'N/A'}</h5>
                      <p className="text-muted mb-0">ID: {associatedHealthSystems[0].health_system_id || 'N/A'}</p>
                      <Link to={`/admin/health-systems/${associatedHealthSystems[0].health_system_id}`} className="btn btn-sm btn-info">
                        <i className="fa fa-eye me-1"></i> Details
                      </Link>
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div className="col-xl-8">
              <div className="card">
                <div className="card-body">
                  <ul className="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'cases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('cases')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fas fa-file-alt"></i></span>
                        <span className="d-none d-sm-block">Cases</span>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'zoom-visits' ? 'active' : ''}`}
                        onClick={() => setActiveTab('zoom-visits')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fas fa-video"></i></span>
                        <span className="d-none d-sm-block">Zoom Visits</span>
                      </a>
                    </li>
                     <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'access-logs' ? 'active' : ''}`}
                        onClick={() => setActiveTab('access-logs')}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="d-block d-sm-none"><i className="fas fa-history"></i></span>
                        <span className="d-none d-sm-block">Access Logs</span>
                      </a>
                    </li>
                  </ul>

                  <div className="tab-content p-3 text-muted">
                    {/* Cases Tab */}
                    <div className={`tab-pane ${activeTab === 'cases' ? 'active' : ''}`} role="tabpanel">
                      <h5 className="font-size-16 mb-3">Associated Cases</h5>
                       {loadingCases ? (
                          <p>Loading associated cases...</p>
                       ) : casesError ? (
                          <div className="alert alert-danger">{casesError}</div>
                       ) : associatedCases.length === 0 ? (
                          <div className="alert alert-info">No associated cases found.</div>
                       ) : (
                          <div className="table-responsive">
                             <table className="table table-centered table-nowrap mb-0">
                                <thead>
                                   <tr>
                                      <th>Case ID</th>
                                      <th>Provider</th>
                                      <th>Health System</th>
                                      <th>Status</th>
                                      <th>Created</th>
                                      <th>Updated</th>
                                      <th>Actions</th>
                                   </tr>
                                </thead>
                                <tbody>
                                   {associatedCases.map(caseItem => (
                                      <tr key={caseItem.id}>
                                         <td>{caseItem.CoreServer_CaseID || caseItem.id}</td>
                                         <td>{caseItem.provider_info_fk ? `${caseItem.provider_info_fk.first_name} ${caseItem.provider_info_fk.last_name}` : 'N/A'}</td>
                                         <td>{caseItem.healthcare_info_fk ? caseItem.healthcare_info_fk.name : 'N/A'}</td>
                                         <td>
                                            {/* Assuming a status field exists and using a similar badge logic as CasesPage */}
                                            <span className={`badge ${
                                              caseItem.mira_sol_case_status === 'completed' ? 'bg-success' :
                                              caseItem.mira_sol_case_status === 'expired' ? 'bg-danger' : 'bg-info'
                                            }`}>
                                              {caseItem.mira_sol_case_status || 'N/A'}
                                            </span>
                                         </td>
                                         <td>{formatDate(caseItem.created_at)}</td>
                                         <td>{formatDate(caseItem.updated_at)}</td>
                                         <td>
                                            <Link to={`/admin/cases/${caseItem.id}`} className="btn btn-sm btn-primary">View</Link>
                                         </td>
                                      </tr>
                                   ))}
                                </tbody>
                             </table>
                          </div>
                       )}
                    </div>

                    {/* Zoom Visits Tab */}
                    <div className={`tab-pane ${activeTab === 'zoom-visits' ? 'active' : ''}`} role="tabpanel">
                      <h5 className="font-size-16 mb-3">Associated Zoom Visits</h5>
                       {loadingZoomVisits ? (
                          <p>Loading associated Zoom visits...</p>
                       ) : zoomVisitsError ? (
                          <div className="alert alert-danger">{zoomVisitsError}</div>
                       ) : associatedZoomVisits.length === 0 ? (
                          <div className="alert alert-info">No associated Zoom visits found.</div>
                       ) : (
                          <div className="table-responsive">
                             <table className="table table-centered table-nowrap mb-0">
                                <thead>
                                   <tr>
                                      <th>Meeting ID</th>
                                      <th>Case ID</th>
                                      <th>Start Time</th>
                                      <th>End Time</th>
                                      <th>Duration</th>
                                      <th>Actions</th>
                                   </tr>
                                </thead>
                                <tbody>
                                   {associatedZoomVisits.map(visit => (
                                      <tr key={visit.id}>
                                         <td>{visit.meeting_id}</td>
                                         <td>{visit.mira_sol_case ? visit.mira_sol_case.CoreServer_CaseID : 'N/A'}</td>
                                         <td>{formatDate(visit.start_time)}</td>
                                         <td>{formatDate(visit.end_time)}</td>
                                         <td>{visit.duration || 'N/A'}</td> {/* Assuming duration is available */}
                                         <td>
                                            {/* Add action buttons if needed, e.g., Link to meeting details */}
                                            <button className="btn btn-sm btn-secondary">View Details</button>
                                         </td>
                                      </tr>
                                   ))}
                                </tbody>
                             </table>
                          </div>
                       )}
                    </div>

                     {/* Access Logs Tab */}
                    <div className={`tab-pane ${activeTab === 'access-logs' ? 'active' : ''}`} role="tabpanel">
                      <h5 className="font-size-16 mb-3">Access Logs</h5>
                       {loadingAccessLogs ? (
                          <p>Loading access logs...</p>
                       ) : accessLogsError ? (
                          <div className="alert alert-danger">{accessLogsError}</div>
                       ) : accessLogs.length === 0 ? (
                          <div className="alert alert-info">No access logs found for this patient.</div>
                       ) : (
                          <div className="table-responsive">
                             <table className="table table-centered table-nowrap mb-0">
                                <thead>
                                   <tr>
                                      <th>Timestamp</th>
                                      <th>Access Type</th>
                                      <th>Result</th>
                                      <th>Details</th>
                                   </tr>
                                </thead>
                                <tbody>
                                   {accessLogs.map(log => (
                                      <tr key={log.id}>
                                         <td>{formatDate(log.timestamp)}</td>
                                         <td>{log.access_type}</td>
                                         <td>{log.result}</td>
                                         <td>
                                            {/* Display relevant details from log object */}
                                            {log.requested_resource && <p>Resource: {log.requested_resource}</p>}
                                            {log.client_ip_address && <p>IP: {log.client_ip_address}</p>}
                                            {log.error_details && Object.keys(log.error_details).length > 0 && (
                                              <p>Error: {JSON.stringify(log.error_details)}</p>
                                            )}
                                            {/* Add more details as needed */}
                                         </td>
                                      </tr>
                                   ))}
                                </tbody>
                             </table>
                          </div>
                       )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="alert alert-info">Patient details not found.</div>
        )}
      </div>
    </>
  );
};

export default PatientDetailsPage;